import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Row, Col, Card, CardBody, CardHeader, Button, Spinner, Alert,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { formatFileSize } from 'util/resourceFiles';

const AnkiDecks = ({ activeSpecialty }) => {
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Specialty must be sent — the backend defaults to pmr without it,
        // which would show PM&R decks while viewing another specialty
        const res = await axios.post('/fetchMyDeckFiles', { specialty: activeSpecialty });
        if (!cancelled) {
          setHasAccess(!!res.data.hasAccess);
          setFiles(res.data.files || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'We could not load your decks.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
    // Decks and access are both per specialty — refetch whenever it changes
  }, [activeSpecialty]);

  const handleDownload = async (file) => {
    setDownloadingId(file.fileId);
    try {
      const res = await axios.post('/fetchMyDeckFileUrl', {
        fileId: file.fileId,
        specialty: activeSpecialty,
      });
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      Swal.fire({
        title: 'Download failed',
        text: err.response?.data?.error || 'Please try again.',
        icon: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <div className="header bg-gradient-success pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <h1 className="text-white mb-0">Anki Decks</h1>
            <p className="text-white mt-0 mb-0">
              Download the latest flashcard decks — updated as new cards are added
            </p>
          </div>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row>
          <Col xl="10">
            <Card className="shadow mb-5">
              <CardHeader className="border-0">
                <h3 className="mb-0">Your Decks</h3>
              </CardHeader>
              <CardBody>
                {loading && (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                )}

                {!loading && error && <Alert color="danger">{error}</Alert>}

                {/* Matches the lock design used on the PM&R Review Guide page */}
                {!loading && !error && !hasAccess && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' }}>
                    <i className="fas fa-lock text-warning" style={{ fontSize: '40px', marginBottom: '15px' }}></i>
                    <h3 className="text-dark mb-2">Access Required</h3>
                    <p className="text-muted mb-3 text-center" style={{ maxWidth: '300px', fontSize: '14px' }}>
                      Subscribe to access the Anki Decks.
                    </p>
                    <Button
                      color="success"
                      onClick={() => history.push('/client/plans')}
                    >
                      <i className="fas fa-crown mr-2"></i>
                      View Plans
                    </Button>
                  </div>
                )}

                {!loading && !error && hasAccess && files.length === 0 && (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-layer-group fa-3x mb-3" />
                    <h4>No decks yet</h4>
                    <p className="mb-0">New decks will appear here as soon as they're published.</p>
                  </div>
                )}

                {!loading && !error && hasAccess && files.length > 0 && (
                  <>
                    <Alert color="info" className="py-2">
                      <small>
                        <i className="fas fa-sync-alt mr-2" />
                        Download as often as you like — grab the file again whenever a deck is updated.
                      </small>
                    </Alert>

                    {files.map((file) => (
                      <div
                        key={file.fileId}
                        className="d-flex align-items-center justify-content-between py-3 border-bottom"
                      >
                        <div className="mr-3">
                          <h5 className="mb-1">{file.title}</h5>
                          {file.description && (
                            <p className="text-muted mb-1"><small>{file.description}</small></p>
                          )}
                          <small className="text-muted">
                            {formatFileSize(file.sizeBytes)}
                            {file.uploadedAt && (
                              <>
                                <span className="mx-2">·</span>
                                Updated {new Date(file.uploadedAt).toLocaleDateString()}
                              </>
                            )}
                          </small>
                        </div>
                        <Button
                          color="primary"
                          onClick={() => handleDownload(file)}
                          disabled={downloadingId === file.fileId}
                        >
                          {downloadingId === file.fileId ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="fas fa-download mr-2" />
                              Download
                            </>
                          )}
                        </Button>
                      </div>
                    ))}

                    <div className="mt-4">
                      <h5>New to Anki?</h5>
                      <ol className="text-muted" style={{ fontSize: '0.9rem' }}>
                        <li>Install Anki free from apps.ankiweb.net, or AnkiMobile / AnkiDroid on your phone.</li>
                        <li>Open the downloaded .apkg file - Anki imports it automatically.</li>
                        <li>Review daily. Short sessions every day beat long ones once a week.</li>
                      </ol>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  activeSpecialty: state.specialty.activeSpecialty,
});

export default connect(mapStateToProps)(AnkiDecks);
